import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import {
  Plus,
  Search,
  TrendingDown,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  RefreshCw,
  CreditCard,
  Upload,
  FileText,
  Ban,
  Repeat,
  X
} from 'lucide-react';
import { despesaService, configuracoesFinanceiroService, formatCurrency, formatDate } from '@/services';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const Despesas = () => {
  const { toast } = useToast();

  // Estados principais
  const [despesas, setDespesas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroCategoria, setFiltroCategoria] = useState('todos');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [stats, setStats] = useState(null);

  // Estados de modais
  const [modalOpen, setModalOpen] = useState(false);
  const [pagamentoModalOpen, setPagamentoModalOpen] = useState(false);
  const [detalhesModalOpen, setDetalhesModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [despesaSelecionada, setDespesaSelecionada] = useState(null);
  const [modoEdicao, setModoEdicao] = useState(false);

  // Upload
  const [uploadingAnexo, setUploadingAnexo] = useState(false);

  // Estados do formulário
  const [formData, setFormData] = useState({
    fornecedor: '',
    cnpj_cpf_fornecedor: '',
    descricao: '',
    categoria_id: '',
    centro_custo: '',
    valor_original: '',
    valor_desconto: '0',
    data_emissao: new Date().toISOString().split('T')[0],
    data_vencimento: '',
    metodo_pagamento: 'boleto',
    recorrente: false,
    tipo_recorrencia: 'mensal',
    tipo_despesa: 'variavel',
    observacoes: '',
    tags: []
  });

  // Estados do formulário de pagamento
  const [pagamentoData, setPagamentoData] = useState({
    data_pagamento: new Date().toISOString().split('T')[0],
    valor_pago: '',
    metodo_pagamento: 'boleto',
    comprovante_url: '',
    observacoes: ''
  });

  useEffect(() => {
    loadDespesas();
    loadStats();
    loadCategorias();
  }, []);

  const loadDespesas = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroStatus !== 'todos') params.status = filtroStatus;
      if (filtroTipo !== 'todos') params.tipo_despesa = filtroTipo;
      if (filtroCategoria !== 'todos') params.categoria_id = filtroCategoria;
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFim) params.data_fim = dataFim;

      const data = await despesaService.getAll(params);
      setDespesas(data.despesas || []);
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await despesaService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const loadCategorias = async () => {
    try {
      const data = await configuracoesFinanceiroService.getCategorias('despesa', true);
      setCategorias(data.categorias || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (!formData.fornecedor || !formData.descricao || !formData.valor_original || !formData.data_vencimento) {
        toast({
          title: 'Erro',
          description: 'Preencha todos os campos obrigatórios',
          variant: 'destructive',
        });
        return;
      }

      const dados = {
        ...formData,
        valor_original: parseFloat(formData.valor_original),
        valor_desconto: parseFloat(formData.valor_desconto || 0),
      };

      if (modoEdicao && despesaSelecionada) {
        const despesaId = despesaSelecionada.id || despesaSelecionada._id;
        if (!despesaId) {
          toast({
            title: 'Erro',
            description: 'ID da despesa não encontrado. Objeto:', 
            variant: 'destructive',
          });
          console.error('despesaSelecionada sem ID:', despesaSelecionada);
          return;
        }
        
        await despesaService.update(despesaId, dados);
        toast({
          title: 'Sucesso',
          description: 'Despesa atualizada com sucesso',
        });
      } else {
        await despesaService.create(dados);
        toast({
          title: 'Sucesso',
          description: 'Despesa criada com sucesso',
        });
      }

      setModalOpen(false);
      resetForm();
      loadDespesas();
      loadStats();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleRegistrarPagamento = async (e) => {
    e.preventDefault();
    
    try {
      if (!despesaSelecionada || !despesaSelecionada._id) {
        toast({
          title: 'Erro',
          description: 'Nenhuma despesa selecionada',
          variant: 'destructive',
        });
        return;
      }

      if (!pagamentoData.valor_pago) {
        toast({
          title: 'Erro',
          description: 'Informe o valor pago',
          variant: 'destructive',
        });
        return;
      }

      const dados = {
        ...pagamentoData,
        valor_pago: parseFloat(pagamentoData.valor_pago),
      };

      await despesaService.registrarPagamento(despesaSelecionada._id, dados);
      
      toast({
        title: 'Sucesso',
        description: 'Pagamento registrado com sucesso',
      });

      setPagamentoModalOpen(false);
      setDespesaSelecionada(null);
      resetPagamentoForm();
      loadDespesas();
      loadStats();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleToggleRecorrencia = async (despesa) => {
    try {
      if (!despesa) {
        toast({
          title: 'Erro',
          description: 'Despesa inválida',
          variant: 'destructive',
        });
        return;
      }

      const despesaId = despesa.id || despesa._id;
      if (!despesaId) {
        toast({
          title: 'Erro',
          description: 'ID da despesa não encontrado',
          variant: 'destructive',
        });
        return;
      }

      await despesaService.toggleRecorrencia(despesaId, !despesa.recorrencia_ativa);
      toast({
        title: 'Sucesso',
        description: `Recorrência ${!despesa.recorrencia_ativa ? 'ativada' : 'desativada'} com sucesso`,
      });
      loadDespesas();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (despesa) => {
    console.log('handleEdit called with despesa:', despesa);
    setDespesaSelecionada(despesa);
    setModoEdicao(true);
    setFormData({
      fornecedor: despesa.fornecedor || '',
      cnpj_cpf_fornecedor: despesa.cnpj_cpf_fornecedor || '',
      descricao: despesa.descricao || '',
      categoria_id: despesa.categoria_id || '',
      centro_custo: despesa.centro_custo || '',
      valor_original: despesa.valor_original?.toString() || '',
      valor_desconto: despesa.valor_desconto?.toString() || '0',
      data_emissao: despesa.data_emissao?.split('T')[0] || '',
      data_vencimento: despesa.data_vencimento?.split('T')[0] || '',
      metodo_pagamento: despesa.metodo_pagamento || 'boleto',
      recorrente: despesa.recorrente || false,
      tipo_recorrencia: despesa.tipo_recorrencia || 'mensal',
      tipo_despesa: despesa.tipo_despesa || 'variavel',
      observacoes: despesa.observacoes || '',
      tags: despesa.tags || []
    });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      if (!despesaSelecionada) {
        toast({
          title: 'Erro',
          description: 'Nenhuma despesa selecionada',
          variant: 'destructive',
        });
        return;
      }

      const despesaId = despesaSelecionada.id || despesaSelecionada._id;
      if (!despesaId) {
        toast({
          title: 'Erro',
          description: 'ID da despesa não encontrado',
          variant: 'destructive',
        });
        return;
      }

      await despesaService.delete(despesaId);
      toast({
        title: 'Sucesso',
        description: 'Despesa cancelada com sucesso',
      });
      setDeleteDialogOpen(false);
      setDespesaSelecionada(null);
      loadDespesas();
      loadStats();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleVerDetalhes = async (despesa) => {
    try {
      const detalhes = await despesaService.getById(despesa._id);
      setDespesaSelecionada(detalhes);
      setDetalhesModalOpen(true);
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handlePagarDespesa = (despesa) => {
    setDespesaSelecionada(despesa);
    setPagamentoData({
      ...pagamentoData,
      valor_pago: despesa.valor_final?.toString() || despesa.valor_original?.toString() || ''
    });
    setPagamentoModalOpen(true);
  };

  const handleUploadAnexo = async (despesaId, file) => {
    setUploadingAnexo(true);
    try {
      await despesaService.uploadAnexo(despesaId, file);
      toast({
        title: 'Sucesso',
        description: 'Comprovante enviado com sucesso',
      });
      const detalhes = await despesaService.getById(despesaId);
      setDespesaSelecionada(detalhes);
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    } finally {
      setUploadingAnexo(false);
    }
  };

  const handleDeleteAnexo = async (despesaId, anexoId) => {
    try {
      await despesaService.deleteAnexo(despesaId, anexoId);
      toast({
        title: 'Sucesso',
        description: 'Anexo removido com sucesso',
      });
      const detalhes = await despesaService.getById(despesaId);
      setDespesaSelecionada(detalhes);
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      fornecedor: '',
      cnpj_cpf_fornecedor: '',
      descricao: '',
      categoria_id: '',
      centro_custo: '',
      valor_original: '',
      valor_desconto: '0',
      data_emissao: new Date().toISOString().split('T')[0],
      data_vencimento: '',
      metodo_pagamento: 'boleto',
      recorrente: false,
      tipo_recorrencia: 'mensal',
      tipo_despesa: 'variavel',
      observacoes: '',
      tags: []
    });
    setModoEdicao(false);
    setDespesaSelecionada(null);
  };

  const resetPagamentoForm = () => {
    setPagamentoData({
      data_pagamento: new Date().toISOString().split('T')[0],
      valor_pago: '',
      metodo_pagamento: 'boleto',
      comprovante_url: '',
      observacoes: ''
    });
  };

  const getStatusBadge = (despesa) => {
    const status = despesa.status;
    const configs = {
      pago: { label: 'Pago', className: 'bg-green-100 text-green-800 hover:bg-green-100', icon: CheckCircle },
      em_aberto: { label: 'Em Aberto', className: 'bg-blue-100 text-blue-800 hover:bg-blue-100', icon: Clock },
      atrasado: { label: 'Atrasado', className: 'bg-red-100 text-red-800 hover:bg-red-100', icon: AlertCircle },
      parcial: { label: 'Pago Parcial', className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100', icon: Clock },
      cancelado: { label: 'Cancelado', className: 'bg-gray-100 text-gray-800 hover:bg-gray-100', icon: Ban }
    };

    const config = configs[status] || configs.em_aberto;
    const Icon = config.icon;

    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getTipoBadge = (tipo) => {
    const configs = {
      fixa: { label: 'Fixa', className: 'bg-purple-100 text-purple-800' },
      variavel: { label: 'Variável', className: 'bg-blue-100 text-blue-800' },
      investimento: { label: 'Investimento', className: 'bg-green-100 text-green-800' }
    };

    const config = configs[tipo] || configs.variavel;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  const calcularDiasAtraso = (dataVencimento) => {
    const hoje = new Date();
    const vencimento = new Date(dataVencimento);
    const diffTime = hoje - vencimento;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Filtrar despesas
  const despesasFiltradas = despesas.filter(despesa => {
    const matchSearch = despesa.fornecedor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       despesa.numero_lancamento?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       despesa.descricao?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchSearch;
  });

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-gray-500">Carregando despesas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Despesas / Contas a Pagar</h1>
          <p className="text-gray-500 mt-1">Gerencie as despesas e contas a pagar da empresa</p>
        </div>
        <Button onClick={() => { resetForm(); setModalOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Despesa
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total a Pagar</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(stats.total_a_pagar)}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-blue-600" />
              </div>
              <p className="text-xs text-gray-500 mt-2">{stats.despesas_abertas} despesas em aberto</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Pago</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(stats.total_pago)}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-xs text-gray-500 mt-2">{stats.despesas_pagas} despesas pagas</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Atrasadas</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(stats.total_atrasado)}
                  </p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <p className="text-xs text-gray-500 mt-2">{stats.despesas_atrasadas} despesas atrasadas</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total no Mês</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {formatCurrency(stats.total_a_pagar + stats.total_pago)}
                  </p>
                </div>
                <TrendingDown className="h-8 w-8 text-purple-600" />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {stats.total_despesas_mes} despesas no mês
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por fornecedor, número ou descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <Select value={filtroStatus} onValueChange={(value) => { setFiltroStatus(value); loadDespesas(); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os Status</SelectItem>
                <SelectItem value="em_aberto">Em Aberto</SelectItem>
                <SelectItem value="atrasado">Atrasadas</SelectItem>
                <SelectItem value="pago">Pagas</SelectItem>
                <SelectItem value="parcial">Pago Parcial</SelectItem>
                <SelectItem value="cancelado">Canceladas</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filtroTipo} onValueChange={(value) => { setFiltroTipo(value); loadDespesas(); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os Tipos</SelectItem>
                <SelectItem value="fixa">Fixa</SelectItem>
                <SelectItem value="variavel">Variável</SelectItem>
                <SelectItem value="investimento">Investimento</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={loadDespesas}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
          </div>

          {/* Filtro de Categoria e Data */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <Select value={filtroCategoria} onValueChange={(value) => { setFiltroCategoria(value); loadDespesas(); }}>
              <SelectTrigger>
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Todas as Categorias" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas as Categorias</SelectItem>
                {categorias.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              placeholder="Data Início"
            />

            <Input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              placeholder="Data Fim"
            />
          </div>
        </CardContent>
      </Card>

      {/* Lista de Despesas */}
      {despesasFiltradas.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <TrendingDown className="h-16 w-16 text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">Nenhuma despesa encontrada</p>
            <p className="text-gray-400 text-sm mt-2">
              {searchTerm || filtroStatus !== 'todos' || filtroTipo !== 'todos'
                ? 'Tente ajustar os filtros'
                : 'Crie sua primeira despesa clicando no botão acima'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {despesasFiltradas.map((despesa) => {
            const diasAtraso = calcularDiasAtraso(despesa.data_vencimento);

            return (
              <Card key={despesa.id} className={`hover:shadow-lg transition-shadow ${
                despesa.status === 'atrasado' ? 'border-red-200 bg-red-50' : ''
              }`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    {/* Informações Principais */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {despesa.numero_lancamento}
                        </h3>
                        {getStatusBadge(despesa)}
                        {getTipoBadge(despesa.tipo_despesa)}
                        {despesa.recorrente && (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            <Repeat className="h-3 w-3 mr-1" />
                            Recorrente
                          </Badge>
                        )}
                        {despesa.status === 'atrasado' && diasAtraso > 0 && (
                          <Badge variant="destructive">
                            {diasAtraso} {diasAtraso === 1 ? 'dia' : 'dias'} de atraso
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-500">Fornecedor</p>
                          <p className="font-medium text-gray-900">{despesa.fornecedor}</p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Descrição</p>
                          <p className="font-medium text-gray-900">{despesa.descricao}</p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Categoria</p>
                          <p className="font-medium text-gray-900">{despesa.categoria_nome || 'Sem categoria'}</p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Vencimento</p>
                          <p className="font-medium text-gray-900 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(despesa.data_vencimento)}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Valor</p>
                          <p className="text-xl font-bold text-gray-900">
                            {formatCurrency(despesa.valor_final || despesa.valor_original)}
                          </p>
                        </div>
                      </div>

                      {/* Data de Pagamento para Pagas */}
                      {despesa.status === 'pago' && despesa.data_pagamento && (
                        <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span>Pago em: {formatDate(despesa.data_pagamento)}</span>
                          </div>
                          <div>
                            <span>Valor pago: </span>
                            <span className="font-semibold">{formatCurrency(despesa.valor_pago)}</span>
                          </div>
                        </div>
                      )}

                      {/* Centro de Custo */}
                      {despesa.centro_custo && (
                        <div className="mt-2">
                          <Badge variant="secondary" className="text-xs">
                            {despesa.centro_custo}
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Ações */}
                    <div className="flex flex-col gap-2">
                      {despesa.status !== 'pago' && despesa.status !== 'cancelado' && (
                        <Button
                          size="sm"
                          onClick={() => handlePagarDespesa(despesa)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Registrar Pagamento
                        </Button>
                      )}

                      {despesa.recorrente && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleRecorrencia(despesa)}
                          className={despesa.recorrencia_ativa ? 'border-green-600 text-green-600' : 'border-gray-300'}
                        >
                          <Repeat className="h-4 w-4 mr-2" />
                          {despesa.recorrencia_ativa ? 'Ativa' : 'Inativa'}
                        </Button>
                      )}

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleVerDetalhes(despesa)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Ver Detalhes
                          </DropdownMenuItem>
                          {despesa.status !== 'pago' && (
                            <>
                              <DropdownMenuItem onClick={() => handleEdit(despesa)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setDespesaSelecionada(despesa);
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Cancelar
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal de Criar/Editar Despesa */}
      <Dialog open={modalOpen} onOpenChange={(open) => {
        setModalOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {modoEdicao ? 'Editar Despesa' : 'Nova Despesa'}
            </DialogTitle>
            <DialogDescription>
              {modoEdicao ? 'Atualize as informações da despesa' : 'Preencha os dados da nova despesa'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Fornecedor e CNPJ/CPF */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fornecedor">Fornecedor *</Label>
                <Input
                  id="fornecedor"
                  value={formData.fornecedor}
                  onChange={(e) => setFormData({ ...formData, fornecedor: e.target.value })}
                  placeholder="Nome do fornecedor"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cnpj_cpf_fornecedor">CNPJ/CPF</Label>
                <Input
                  id="cnpj_cpf_fornecedor"
                  value={formData.cnpj_cpf_fornecedor}
                  onChange={(e) => setFormData({ ...formData, cnpj_cpf_fornecedor: e.target.value })}
                  placeholder="00.000.000/0000-00"
                />
              </div>
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição *</Label>
              <Input
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Ex: Aluguel Dezembro/2024"
                required
              />
            </div>

            {/* Categoria, Centro de Custo e Tipo */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="categoria_id">Categoria</Label>
                <Select
                  value={formData.categoria_id}
                  onValueChange={(value) => setFormData({ ...formData, categoria_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {categorias.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="centro_custo">Centro de Custo</Label>
                <Input
                  id="centro_custo"
                  value={formData.centro_custo}
                  onChange={(e) => setFormData({ ...formData, centro_custo: e.target.value })}
                  placeholder="Ex: TI, Marketing"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tipo_despesa">Tipo de Despesa *</Label>
                <Select
                  value={formData.tipo_despesa}
                  onValueChange={(value) => setFormData({ ...formData, tipo_despesa: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixa">Fixa</SelectItem>
                    <SelectItem value="variavel">Variável</SelectItem>
                    <SelectItem value="investimento">Investimento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Valores */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valor_original">Valor Original *</Label>
                <Input
                  id="valor_original"
                  type="number"
                  step="0.01"
                  value={formData.valor_original}
                  onChange={(e) => setFormData({ ...formData, valor_original: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="valor_desconto">Desconto</Label>
                <Input
                  id="valor_desconto"
                  type="number"
                  step="0.01"
                  value={formData.valor_desconto}
                  onChange={(e) => setFormData({ ...formData, valor_desconto: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Datas e Método */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="data_emissao">Data de Emissão *</Label>
                <Input
                  id="data_emissao"
                  type="date"
                  value={formData.data_emissao}
                  onChange={(e) => setFormData({ ...formData, data_emissao: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="data_vencimento">Data de Vencimento *</Label>
                <Input
                  id="data_vencimento"
                  type="date"
                  value={formData.data_vencimento}
                  onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="metodo_pagamento">Método de Pagamento</Label>
                <Select
                  value={formData.metodo_pagamento}
                  onValueChange={(value) => setFormData({ ...formData, metodo_pagamento: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="boleto">Boleto</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                    <SelectItem value="transferencia">Transferência</SelectItem>
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                    <SelectItem value="debito_automatico">Débito Automático</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Recorrência */}
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="recorrente">Despesa Recorrente</Label>
                  <p className="text-xs text-gray-500">Gerar automaticamente nos próximos meses</p>
                </div>
                <Switch
                  id="recorrente"
                  checked={formData.recorrente}
                  onCheckedChange={(checked) => setFormData({ ...formData, recorrente: checked })}
                />
              </div>

              {formData.recorrente && (
                <div className="space-y-2">
                  <Label htmlFor="tipo_recorrencia">Tipo de Recorrência</Label>
                  <Select
                    value={formData.tipo_recorrencia}
                    onValueChange={(value) => setFormData({ ...formData, tipo_recorrencia: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensal">Mensal</SelectItem>
                      <SelectItem value="bimestral">Bimestral</SelectItem>
                      <SelectItem value="trimestral">Trimestral</SelectItem>
                      <SelectItem value="semestral">Semestral</SelectItem>
                      <SelectItem value="anual">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Observações */}
            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Notas internas sobre esta despesa"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {modoEdicao ? 'Atualizar' : 'Criar'} Despesa
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Registrar Pagamento */}
      <Dialog open={pagamentoModalOpen} onOpenChange={setPagamentoModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Pagamento</DialogTitle>
            <DialogDescription>
              {despesaSelecionada?.numero_lancamento} - {despesaSelecionada?.fornecedor}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleRegistrarPagamento} className="space-y-4">
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600">Valor da Despesa</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(despesaSelecionada?.valor_final || despesaSelecionada?.valor_original)}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="data_pagamento">Data do Pagamento *</Label>
              <Input
                id="data_pagamento"
                type="date"
                value={pagamentoData.data_pagamento}
                onChange={(e) => setPagamentoData({ ...pagamentoData, data_pagamento: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="valor_pago">Valor Pago *</Label>
              <Input
                id="valor_pago"
                type="number"
                step="0.01"
                value={pagamentoData.valor_pago}
                onChange={(e) => setPagamentoData({ ...pagamentoData, valor_pago: e.target.value })}
                placeholder="0.00"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="metodo_pagamento_pag">Método de Pagamento *</Label>
              <Select
                value={pagamentoData.metodo_pagamento}
                onValueChange={(value) => setPagamentoData({ ...pagamentoData, metodo_pagamento: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="boleto">Boleto</SelectItem>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                  <SelectItem value="transferencia">Transferência</SelectItem>
                  <SelectItem value="dinheiro">Dinheiro</SelectItem>
                  <SelectItem value="debito_automatico">Débito Automático</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comprovante_url">Link do Comprovante (opcional)</Label>
              <Input
                id="comprovante_url"
                value={pagamentoData.comprovante_url}
                onChange={(e) => setPagamentoData({ ...pagamentoData, comprovante_url: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="observacoes_pag">Observações</Label>
              <Textarea
                id="observacoes_pag"
                value={pagamentoData.observacoes}
                onChange={(e) => setPagamentoData({ ...pagamentoData, observacoes: e.target.value })}
                placeholder="Notas sobre o pagamento"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPagamentoModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirmar Pagamento
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalhes */}
      <Dialog open={detalhesModalOpen} onOpenChange={setDetalhesModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{despesaSelecionada?.numero_lancamento}</span>
              {despesaSelecionada && getStatusBadge(despesaSelecionada)}
            </DialogTitle>
            <DialogDescription>
              {despesaSelecionada?.fornecedor}
            </DialogDescription>
          </DialogHeader>

          {despesaSelecionada && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Categoria</Label>
                  <p className="font-medium">{despesaSelecionada.categoria_nome || 'Sem categoria'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Tipo</Label>
                  <p className="font-medium capitalize">{despesaSelecionada.tipo_despesa}</p>
                </div>
              </div>

              {despesaSelecionada.centro_custo && (
                <div>
                  <Label className="text-gray-500">Centro de Custo</Label>
                  <p className="font-medium">{despesaSelecionada.centro_custo}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-500">Valor Original</Label>
                  <p className="text-lg font-bold">{formatCurrency(despesaSelecionada.valor_original)}</p>
                </div>
                {despesaSelecionada.valor_desconto > 0 && (
                  <div>
                    <Label className="text-gray-500">Desconto</Label>
                    <p className="text-lg font-bold text-green-600">
                      -{formatCurrency(despesaSelecionada.valor_desconto)}
                    </p>
                  </div>
                )}
                <div>
                  <Label className="text-gray-500">Valor Final</Label>
                  <p className="text-lg font-bold text-purple-600">
                    {formatCurrency(despesaSelecionada.valor_final || despesaSelecionada.valor_original)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Data de Emissão</Label>
                  <p className="font-medium">{formatDate(despesaSelecionada.data_emissao)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Data de Vencimento</Label>
                  <p className="font-medium">{formatDate(despesaSelecionada.data_vencimento)}</p>
                </div>
              </div>

              {despesaSelecionada.recorrente && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <Label className="text-purple-900">Despesa Recorrente</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <p className="text-sm text-purple-700">Tipo</p>
                      <p className="font-semibold capitalize">{despesaSelecionada.tipo_recorrencia}</p>
                    </div>
                    <div>
                      <p className="text-sm text-purple-700">Status</p>
                      <p className="font-semibold">{despesaSelecionada.recorrencia_ativa ? 'Ativa' : 'Inativa'}</p>
                    </div>
                  </div>
                </div>
              )}

              {despesaSelecionada.data_pagamento && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <Label className="text-green-900">Pagamento Realizado</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <p className="text-sm text-green-700">Data</p>
                      <p className="font-semibold">{formatDate(despesaSelecionada.data_pagamento)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-green-700">Valor Pago</p>
                      <p className="font-semibold">{formatCurrency(despesaSelecionada.valor_pago)}</p>
                    </div>
                  </div>
                </div>
              )}

              {despesaSelecionada.observacoes && (
                <div>
                  <Label className="text-gray-500">Observações</Label>
                  <p className="text-gray-700 mt-1">{despesaSelecionada.observacoes}</p>
                </div>
              )}

              {/* Anexos */}
              <div>
                <Label className="text-gray-500 mb-2 block">Anexos/Comprovantes</Label>
                
                {despesaSelecionada.anexos && despesaSelecionada.anexos.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {despesaSelecionada.anexos.map((anexo) => (
                      <div key={anexo.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{anexo.nome_arquivo}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteAnexo(despesaSelecionada.id || despesaSelecionada._id, anexo.id || anexo._id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    id="file-upload-despesa"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleUploadAnexo(despesaSelecionada.id || despesaSelecionada._id, e.target.files[0]);
                      }
                    }}
                    disabled={uploadingAnexo}
                  />
                  <label
                    htmlFor="file-upload-despesa"
                    className="flex flex-col items-center justify-center cursor-pointer"
                  >
                    {uploadingAnexo ? (
                      <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mb-2" />
                    ) : (
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    )}
                    <p className="text-sm text-gray-600">
                      {uploadingAnexo ? 'Enviando...' : 'Clique para fazer upload de comprovante'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG</p>
                  </label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetalhesModalOpen(false)}>
              Fechar
            </Button>
            {despesaSelecionada?.status !== 'pago' && despesaSelecionada?.status !== 'cancelado' && (
              <>
                <Button onClick={() => { setDetalhesModalOpen(false); handleEdit(despesaSelecionada); }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => { setDetalhesModalOpen(false); handlePagarDespesa(despesaSelecionada); }}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Registrar Pagamento
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação cancelará a despesa <strong>{despesaSelecionada?.numero_lancamento}</strong>.
              A despesa será marcada como cancelada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Sim, Cancelar Despesa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Despesas;
